import { useQuery } from '@tanstack/react-query';

import {
    AssetAllocationApi,
    AssetAllocationCategoryType,
    Configuration,
    SecurityWeight,
    StrategyWeight,
} from '../_openapi/portfolio-optimizer';
import { defaultApiConfig } from './defaultApiConfig';

const getAssetAllocationApi = () => new AssetAllocationApi(new Configuration(defaultApiConfig));

export const useAssetClassExposureChart = (brokerAccountIds: string[], options?: { throwOnError?: boolean }) => {
    return useQuery({
        ...options,
        queryKey: ['assetClassExposureChart', ...brokerAccountIds],
        queryFn: () =>
            getAssetAllocationApi().postAccountAssetAllocationChart({
                categoryType: AssetAllocationCategoryType.ASSET_CLASS_BREAKDOWN,
                requestBody: brokerAccountIds,
            }),
        enabled: brokerAccountIds.length > 0,
    });
};

export const useAssetClassExposureByAccount = (brokerAccountIds: string[], options?: { throwOnError?: boolean }) => {
    return useQuery({
        ...options,
        queryKey: ['assetClassExposureByAccount', ...brokerAccountIds],
        queryFn: () =>
            getAssetAllocationApi().postAccountAssetAllocationByAccount({
                categoryType: AssetAllocationCategoryType.ASSET_CLASS_BREAKDOWN,
                requestBody: brokerAccountIds,
            }),
        enabled: brokerAccountIds.length > 0,
    });
};

/**
 * Look-through asset-class exposure for an arbitrary basket of securities+weights —
 * doesn't need an account or a saved allocation, so it works for a strategy still being edited.
 */
export const useSecurityAssetAllocation = (securityWeights: SecurityWeight[]) => {
    return useQuery({
        queryKey: ['securityAssetAllocation', securityWeights],
        queryFn: () =>
            getAssetAllocationApi().postSecurityAssetAllocation({
                categoryType: AssetAllocationCategoryType.ASSET_CLASS_BREAKDOWN,
                securityWeight: securityWeights,
            }),
        enabled: securityWeights.length > 0,
    });
};

export const useMixedAssetAllocation = (strategyWeights: StrategyWeight[], securityWeights: SecurityWeight[]) => {
    return useQuery({
        queryKey: ['mixedAssetAllocation', strategyWeights, securityWeights],
        queryFn: () =>
            getAssetAllocationApi().postMixedAssetAllocation({
                categoryType: AssetAllocationCategoryType.ASSET_CLASS_BREAKDOWN,
                mixedAssetAllocationRequest: { strategyWeights, securityWeights },
            }),
        enabled: strategyWeights.length > 0 || securityWeights.length > 0,
    });
};
